import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  LabelList,
} from 'recharts';
import { CategoryDiversitySpend } from '@/hooks/useDiversityAnalytics';

interface DiversityByCategoryChartProps {
  data: CategoryDiversitySpend[] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatCurrencyShort(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function DiversityByCategoryChart({ data, isLoading }: DiversityByCategoryChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Diversity Spend by Category</CardTitle>
          <CardDescription>
            Compare diversity spend across product categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No category data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = data.map(item => ({
    category: item.categoryLabel,
    diverse: item.diverseSpendCents / 100,
    nonDiverse: item.nonDiverseSpendCents / 100,
    diverseCents: item.diverseSpendCents,
    nonDiverseCents: item.nonDiverseSpendCents,
    totalCents: item.totalSpendCents,
    diversePercentage: item.diversePercentage,
    diverseJobs: item.diverseJobCount,
    totalJobs: item.totalJobCount,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-medium">{label}</p>
          <div className="mt-2 space-y-1 text-sm">
            <p className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Diverse Spend: <span className="font-medium">{formatCurrency(data.diverseCents)}</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gray-300" />
              Non-Diverse: <span className="font-medium">{formatCurrency(data.nonDiverseCents)}</span>
            </p>
            <p className="border-t pt-1 mt-1">
              Diversity Rate: <span className="font-medium">{formatPercentage(data.diversePercentage)}</span>
            </p>
            <p className="text-muted-foreground">
              {data.diverseJobs} of {data.totalJobs} jobs
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom label to show percentage on bars
  const renderPercentageLabel = (props: any) => {
    const { x, y, width, height, value, index } = props;
    const item = chartData[index];
    if (item && item.diversePercentage > 0) {
      return (
        <text
          x={x + width + 5}
          y={y + height / 2}
          fill="hsl(var(--foreground))"
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={500}
        >
          {formatPercentage(item.diversePercentage)}
        </text>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Diversity Spend by Category</CardTitle>
        <CardDescription>
          Compare diversity spend across product categories (sorted by diversity %)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 80, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis
                type="number"
                tickFormatter={formatCurrencyShort}
              />
              <YAxis
                type="category"
                dataKey="category"
                width={120}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                formatter={(value) => {
                  if (value === 'diverse') return 'Diverse Spend';
                  if (value === 'nonDiverse') return 'Non-Diverse Spend';
                  return value;
                }}
              />
              <Bar 
                dataKey="diverse" 
                stackId="spend" 
                fill="hsl(152, 69%, 45%)" 
                name="diverse"
                radius={[0, 0, 0, 0]}
              >
                <LabelList content={renderPercentageLabel} />
              </Bar>
              <Bar 
                dataKey="nonDiverse" 
                stackId="spend" 
                fill="hsl(var(--muted))" 
                name="nonDiverse"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Highlight best and worst categories */}
        {data.length > 1 && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>Highest Diversity:</strong> {data[0].categoryLabel} at {formatPercentage(data[0].diversePercentage)}
              </p>
            </div>
            {data[data.length - 1].diversePercentage < data[0].diversePercentage && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Opportunity:</strong> {data[data.length - 1].categoryLabel} at {formatPercentage(data[data.length - 1].diversePercentage)}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
